import React, { FC } from 'react'
import { Modal, Button, Row } from 'antd'
import { ModalProps } from 'antd/lib/modal'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { change, initialize, submit } from 'redux-form'
import { isEmpty } from 'lodash'

// assets
import { ReactComponent as CloseIcon } from '../../assets/icons/BaseIcons/x-icon.svg'

// forms
import ValidationForm from '../forms/ValidationForm'

// utils
import {
	ELEMENT_ATTRIBUTES,
	LOGICAL_OPERATORS,
	VALIDATION_FORM_FIELD_ARRAYS,
	FORM,
	ELEMENT_TYPES,
	ELEMENTS_WITH_VISIBILITY,
	ELEMENTS_WITH_VISIBILITY_AND_VALIDATION
} from '../../utils/enums'
import { addEmptyRow } from '../../utils/helper'

type Props = ModalProps & {
	isVisible: boolean
	onCancel: any
	disabled?: boolean
	typeOfElementForm: any
	elementLabel: string
	elementType: ELEMENT_TYPES
}

const ValidationModal: FC<Props> = (props) => {
	const { isVisible, onCancel, disabled, elementLabel, typeOfElementForm, elementType } = props
	const { t } = useTranslation()

	const dispatch = useDispatch()

	const handleFormSubmit = async (data: any) => {
		let validation = []
		let requiredAndOptions = []
		let requiredOrOptions = []
		let visibility = []
		let visibilityAndOptions = []
		let visibilityOrOptions = []
		if (ELEMENTS_WITH_VISIBILITY_AND_VALIDATION.includes(elementType)) {
			// concat REQUIRED arrays, if is set some key, add new key 'type'
			requiredAndOptions = data?.[VALIDATION_FORM_FIELD_ARRAYS.REQUIRED_AND_OPTIONS]
				?.map((option: any) => {
					if (option && Object.keys(option).length >= 1) {
						if (option?.value && option?.value !== null) {
							return { ...option, type: LOGICAL_OPERATORS.AND }
						}
						// if attribute value missing or is null add with empty string
						return { ...option, type: LOGICAL_OPERATORS.AND, value: '' }
					}
					return option
				})
				?.filter((condition: any) => !(isEmpty(condition?.value) && isEmpty(condition?.key)))
			requiredOrOptions = data?.[VALIDATION_FORM_FIELD_ARRAYS.REQUIRED_OR_OPTIONS]
				?.map((option: any) => {
					if (option && Object.keys(option).length >= 1) {
						if (option?.value && option?.value !== null) {
							return { ...option, type: LOGICAL_OPERATORS.OR }
						}
						// if attribute value missing or is null add with empty string
						return { ...option, type: LOGICAL_OPERATORS.OR, value: '' }
					}
					return option
				})
				?.filter((condition: any) => !(isEmpty(condition?.value) && isEmpty(condition?.key)))
			// concat validations and remove empty object
			validation = requiredAndOptions.concat(requiredOrOptions).filter((condition: any) => !isEmpty(condition))
		}
		if (ELEMENTS_WITH_VISIBILITY.includes(elementType) || ELEMENTS_WITH_VISIBILITY_AND_VALIDATION.includes(elementType)) {
			// concat READONLY arrays, if is set some key, add new key 'type'
			visibilityAndOptions = data?.[VALIDATION_FORM_FIELD_ARRAYS.VISIBILITY_AND_OPTIONS]
				?.map((option: any) => {
					if (option && Object.keys(option).length >= 1) {
						if (option?.value && option?.value !== null) {
							return { ...option, type: LOGICAL_OPERATORS.AND }
						}
						// if attribute value missing or is null add with empty string
						return { ...option, type: LOGICAL_OPERATORS.AND, value: '' }
					}
					return option
				})
				?.filter((condition: any) => !(isEmpty(condition?.value) && isEmpty(condition?.key)))
			visibilityOrOptions = data?.[VALIDATION_FORM_FIELD_ARRAYS.VISIBILITY_OR_OPTIONS]
				?.map((option: any) => {
					if (option && Object.keys(option).length >= 1) {
						if (option?.value && option?.value !== null) {
							return { ...option, type: LOGICAL_OPERATORS.OR }
						}
						// if attribute value missing or is null add with empty string
						return { ...option, type: LOGICAL_OPERATORS.OR, value: '' }
					}
					return option
				})
				?.filter((condition: any) => !(isEmpty(condition?.value) && isEmpty(condition?.key)))
			// concat visibility and remove empty object
			visibility = visibilityAndOptions.concat(visibilityOrOptions)?.filter((condition: any) => !isEmpty(condition))
		}
		if (ELEMENTS_WITH_VISIBILITY.includes(elementType)) {
			await Promise.all([
				// dispatch form with new concatenated arrays
				dispatch(
					change(typeOfElementForm, ELEMENT_ATTRIBUTES.CONDITIONS, {
						visibility
					})
				),
				// update validation form with fresh data
				dispatch(
					initialize(FORM.VALIDATION_FORM, {
						[VALIDATION_FORM_FIELD_ARRAYS.VISIBILITY_AND_OPTIONS]: [...visibilityAndOptions, ...addEmptyRow(visibilityAndOptions)],
						[VALIDATION_FORM_FIELD_ARRAYS.VISIBILITY_OR_OPTIONS]: [...visibilityOrOptions, ...addEmptyRow(visibilityOrOptions)]
					})
				)
			])
		}
		if (ELEMENTS_WITH_VISIBILITY_AND_VALIDATION.includes(elementType)) {
			await Promise.all([
				// dispatch form with new concatenated arrays
				dispatch(
					change(typeOfElementForm, ELEMENT_ATTRIBUTES.CONDITIONS, {
						validation,
						visibility
					})
				),
				// check if user change value of required button and set same value in rightSideBar of element
				dispatch(change(typeOfElementForm, ELEMENT_ATTRIBUTES.REQUIRED, data?.[ELEMENT_ATTRIBUTES.REQUIRED])),
				// check if user change value of readonly button and set same value in rightSideBar of element
				dispatch(change(typeOfElementForm, ELEMENT_ATTRIBUTES.READ_ONLY, data?.[ELEMENT_ATTRIBUTES.READ_ONLY])),
				// update validation form with fresh data
				dispatch(
					initialize(FORM.VALIDATION_FORM, {
						[VALIDATION_FORM_FIELD_ARRAYS.REQUIRED_AND_OPTIONS]: [...requiredAndOptions, ...addEmptyRow(requiredAndOptions)],
						[VALIDATION_FORM_FIELD_ARRAYS.REQUIRED_OR_OPTIONS]: [...requiredOrOptions, ...addEmptyRow(requiredOrOptions)],
						[VALIDATION_FORM_FIELD_ARRAYS.VISIBILITY_AND_OPTIONS]: [...visibilityAndOptions, ...addEmptyRow(visibilityAndOptions)],
						[VALIDATION_FORM_FIELD_ARRAYS.VISIBILITY_OR_OPTIONS]: [...visibilityOrOptions, ...addEmptyRow(visibilityOrOptions)]
					})
				)
			])
		}
		await dispatch(submit(typeOfElementForm))
		onCancel()
	}

	return (
		<Modal
			closeIcon={<CloseIcon width={18} height={18} />}
			title={t('loc:Validations options')}
			centered
			open={isVisible}
			width={840}
			onCancel={onCancel}
			footer={null}
		>
			<Row justify={'center'} className={'w-full'}>
				<ValidationForm onSubmit={handleFormSubmit} elementLabel={elementLabel} elementType={elementType} />
			</Row>
			<Row justify={'end'} className={'w-full mt-6'}>
				<Button disabled={disabled || false} style={{ width: 124, height: 40 }} key='back' type='text' onClick={onCancel}>
					{t('loc:Close')}
				</Button>
				<Button
					className={'h-10 ant-btn-primary'}
					key='confirm'
					disabled={disabled}
					onClick={() => {
						dispatch(submit(FORM.VALIDATION_FORM))
					}}
				>
					{t('loc:Confirm')}
				</Button>
			</Row>
		</Modal>
	)
}

export default ValidationModal
